import { useMetrics } from '@/lib/metrics';
import { Card, KpiTile, Skeleton } from '@/components';
import { formatMoney, formatPercent } from '@/lib/format';

const IDS = [
  'revenue_net',
  'true_net',
  'net_margin_pct',
  'inventory_at_cost',
  'held_funds_balance',
  'rr_liability',
  'sales_tax_collected',
  'po_balance_due',
];

/**
 * Money, last 30 days. Owner only, and the server enforces that: without
 * `metrics.read_money` these ids are omitted from the response, so this card renders "no
 * data" rather than a zero if it is ever mounted for the wrong role.
 *
 * Cash position is honest about what it is. There is no bank feed at launch, so it reads
 * inventory at WAC plus held funds, both shown, and becomes cash on hand when QBO connects.
 */
export function Money30d() {
  const metrics = useMetrics('home-money', IDS, '30d', undefined, { staleTime: 900_000 });

  if (metrics.isLoading) {
    return (
      <Card title="Money, last 30 days">
        <Skeleton shape="card" lines={2} label="Loading money" />
      </Card>
    );
  }

  const revenue = metrics.get('revenue_net');
  const trueNet = metrics.get('true_net');
  const margin = metrics.get('net_margin_pct');
  const inventory = metrics.get('inventory_at_cost');
  const held = metrics.get('held_funds_balance');
  const rr = metrics.get('rr_liability');
  const tax = metrics.get('sales_tax_collected');
  const po = metrics.get('po_balance_due');

  const liabilities = [rr, tax, po]
    .map((envelope) => envelope?.value ?? 0)
    .reduce((total, value) => total + value, 0);

  return (
    <Card
      title="Money, last 30 days"
      asOf={metrics.asOf}
      ageClass="snapshot"
      error={metrics.error ? 'Money did not refresh. These are the last known numbers.' : null}
      onRetry={metrics.refetch}
      id="money-30d"
    >
      <div className="o-grid o-grid--tiles">
        <KpiTile label="Revenue net" envelope={revenue ?? null} />
        <KpiTile
          label="True net"
          envelope={trueNet ?? null}
          tone={(trueNet?.value ?? 0) >= 0 ? 'ok' : 'crit'}
          note={margin ? `${formatPercent(margin.value)} net margin` : null}
        />
        <KpiTile
          label="Cash position"
          envelope={inventory ?? null}
          note={`inventory at WAC plus held funds ${formatMoney(held?.value ?? null)}`}
        />
        <KpiTile
          label="Open liabilities"
          envelope={
            rr
              ? { ...rr, metric_id: 'open_liabilities', value: liabilities }
              : null
          }
          note="RR credit, sales tax accrued, PO commitments"
        />
      </div>
      <p className="o-muted">
        Three margins exist and each is named: gross, contribution and net. The word margin on
        its own is not used anywhere in this console.
      </p>
    </Card>
  );
}
